import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { wikiTocApi } from '../utils/api';
import WikiTocTree, { TreeNode } from './WikiTocTree';
import { useWikiDocs, WikiTOC, findSection, articlesUnder } from '../utils/wikiToc';
import { ArrowLeft, Pencil, Plus, Trash2 } from 'lucide-react';

const WikiContentsPage: React.FC = () => {
    // Unpublished articles still occupy a section, so the counts and the
    // delete warnings have to see them.
    const { sections, articles, loading, error: loadError, refresh } = useWikiDocs({ publishedOnly: false });
    const [error, setError] = useState<string | null>(null);

    const [showCreateToc, setShowCreateToc] = useState(false);
    const [newTocDescription, setNewTocDescription] = useState('');
    const [newTocSummary, setNewTocSummary] = useState('');
    const [editingToc, setEditingToc] = useState<WikiTOC | null>(null);
    const [showCreateSubToc, setShowCreateSubToc] = useState<string | null>(null);
    const [newSubTocName, setNewSubTocName] = useState('');
    const [deletingToc, setDeletingToc] = useState<WikiTOC | null>(null);

    const getArticleCount = (toc: WikiTOC): number => articlesUnder(articles, toc).length;

    /** Sections only — articles are managed from the article editor. */
    const toNodes = (items: WikiTOC[]): TreeNode[] =>
        items.map(item => ({
            id: item.id,
            label: item.description,
            kind: 'section' as const,
            count: getArticleCount(item),
            children: toNodes(item.children || []),
        }));

    const handleCreateToc = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newTocDescription.trim()) return;

        const result = await wikiTocApi.create({
            description: newTocDescription.trim(),
            summary: newTocSummary.trim() || undefined,
            sortOrder: 0,
        });

        if (result.error) {
            setError(result.error);
        } else {
            setShowCreateToc(false);
            setNewTocDescription('');
            setNewTocSummary('');
            refresh();
        }
    };

    const handleEditToc = async (toc: WikiTOC) => {
        const nameInput = document.getElementById('edit-toc-name') as HTMLInputElement | null;
        const summaryInput = document.getElementById('edit-toc-summary') as HTMLTextAreaElement | null;
        const newName = nameInput?.value?.trim() || toc.description;
        // An empty string clears the blurb; undefined would leave it untouched.
        const newSummary = summaryInput?.value?.trim() ?? '';

        if (newName === toc.description && newSummary === (toc.summary || '')) {
            setEditingToc(null);
            return;
        }

        const result = await wikiTocApi.update(toc.id, {
            description: newName,
            summary: newSummary,
        });
        if (result.error) {
            setError(result.error);
        } else {
            setEditingToc(null);
            refresh();
        }
    };

    const handleCreateSubToc = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newSubTocName.trim() || !showCreateSubToc) return;

        const result = await wikiTocApi.create({
            parentId: showCreateSubToc,
            description: newSubTocName.trim(),
            sortOrder: 0,
        });
        if (result.error) {
            setError(result.error);
        } else {
            setShowCreateSubToc(null);
            setNewSubTocName('');
            refresh();
        }
    };

    const handleConfirmDelete = async () => {
        if (!deletingToc) return;

        const result = await wikiTocApi.delete(deletingToc.id);
        if (result.error) {
            setError(result.error);
        } else {
            refresh();
        }
        setDeletingToc(null);
    };

    const renderActions = (node: TreeNode) => {
        const toc = findSection(sections, node.id);
        if (!toc) return null;

        return (
        <>
            <button
                onClick={() => setEditingToc(toc)}
                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                title="Edit section"
            >
                <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
                onClick={() => { setShowCreateSubToc(toc.id); setNewSubTocName(''); }}
                className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded"
                title="Add sub-section"
            >
                <Plus className="w-3.5 h-3.5" />
            </button>
            <button
                onClick={() => setDeletingToc(toc)}
                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                title="Delete section"
            >
                <Trash2 className="w-3.5 h-3.5" />
            </button>
        </>
        );
    };

    return (
        <div className="bx-wrap py-4 fade-in">
            {/* Header */}
            <div className="flex items-start justify-between mb-8 gap-4">
                <div>
                    <Link
                        to="/wiki"
                        className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-2"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to Documentation
                    </Link>
                    <h1 className="text-3xl font-bold text-gray-800">Manage Contents</h1>
                    <p className="text-gray-500 mt-1">Rename, nest and remove documentation sections</p>
                </div>
                <button
                    onClick={() => { setShowCreateToc(true); setNewTocDescription(''); }}
                    className="shrink-0 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors text-sm font-medium"
                >
                    + New Section
                </button>
            </div>

            {(error || loadError) && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {error || loadError}
                </div>
            )}

            <div className="max-w-3xl">
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    {loading ? (
                        <div className="flex justify-center py-8">
                            <div className="spinner w-5 h-5"></div>
                        </div>
                    ) : sections.length === 0 ? (
                        <p className="text-sm text-gray-400 italic px-4 py-6">
                            No sections yet. Create one to get started.
                        </p>
                    ) : (
                        <WikiTocTree nodes={toNodes(sections)} renderActions={renderActions} />
                    )}
                </div>
                <p className="text-xs text-gray-400 mt-3">
                    The number beside a section is how many articles it holds, including its sub-sections.
                </p>
            </div>

            {/* Create Section Modal */}
            {showCreateToc && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 fade-in">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-gray-900">New Section</h2>
                            <button onClick={() => setShowCreateToc(false)} className="text-gray-400 hover:text-gray-600">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <form onSubmit={handleCreateToc} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Section Name *
                                </label>
                                <input
                                    type="text"
                                    value={newTocDescription}
                                    onChange={(e) => setNewTocDescription(e.target.value)}
                                    placeholder="e.g., Getting Started"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                    required
                                    autoFocus
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Summary
                                </label>
                                <textarea
                                    value={newTocSummary}
                                    onChange={(e) => setNewTocSummary(e.target.value)}
                                    placeholder="One or two lines on what this section covers."
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-y"
                                />
                                <p className="text-xs text-gray-400 mt-1">
                                    Shown on the documentation landing page card.
                                </p>
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowCreateToc(false)}
                                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                                >
                                    Create
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Rename Section Modal */}
            {editingToc && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 fade-in">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-gray-900">Edit Section</h2>
                            <button onClick={() => setEditingToc(null)} className="text-gray-400 hover:text-gray-600">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <form onSubmit={(e) => {
                            e.preventDefault();
                            handleEditToc(editingToc);
                        }} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Section Name
                                </label>
                                <input
                                    type="text"
                                    defaultValue={editingToc.description}
                                    id="edit-toc-name"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                    autoFocus
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Summary
                                </label>
                                <textarea
                                    defaultValue={editingToc.summary || ''}
                                    id="edit-toc-summary"
                                    placeholder="One or two lines on what this section covers."
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-y"
                                />
                                <p className="text-xs text-gray-400 mt-1">
                                    Shown on the documentation landing page card.
                                </p>
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setEditingToc(null)}
                                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                                >
                                    Save
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Create Sub-Section Modal */}
            {showCreateSubToc && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 fade-in">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-gray-900">Add Sub-Section</h2>
                            <button onClick={() => setShowCreateSubToc(null)} className="text-gray-400 hover:text-gray-600">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <form onSubmit={handleCreateSubToc} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Sub-Section Name *
                                </label>
                                <input
                                    type="text"
                                    value={newSubTocName}
                                    onChange={(e) => setNewSubTocName(e.target.value)}
                                    placeholder="e.g., Installation"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                    required
                                    autoFocus
                                />
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowCreateSubToc(null)}
                                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors font-medium"
                                >
                                    Add
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deletingToc && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 fade-in">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-gray-900">Delete Section</h2>
                            <button onClick={() => setDeletingToc(null)} className="text-gray-400 hover:text-gray-600">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="space-y-4">
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                <p className="text-sm text-red-800 font-medium">
                                    Are you sure you want to delete "{deletingToc.description}"?
                                </p>
                                {(() => {
                                    const articleCount = getArticleCount(deletingToc);
                                    const childrenCount = deletingToc.children?.length || 0;
                                    const warnings: string[] = [];
                                    if (articleCount > 0) warnings.push(`⚠️ ${articleCount} article(s) will be orphaned.`);
                                    if (childrenCount > 0) warnings.push(`⚠️ ${childrenCount} sub-section(s) will also be deleted.`);
                                    return warnings.length > 0 ? (
                                        <ul className="mt-2 text-xs text-red-700 space-y-1">
                                            {warnings.map((w, i) => <li key={i}>{w}</li>)}
                                        </ul>
                                    ) : null;
                                })()}
                            </div>
                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setDeletingToc(null)}
                                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirmDelete}
                                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WikiContentsPage;
